/**
 * ZustandHistoryMiddleware.ts
 * Middleware Zustand pour gérer l'historique (Undo/Redo) directement dans le store
 * Système stable avec past[], present, future[]
 *
 * RÈGLES:
 * - Toutes les mutations passent par actions: addShape, updateShape, deleteShape, updateShapes
 * - commitHistory() doit être appelé à la fin d'une action utilisateur (drag end, transform end, delete)
 * - Undo/Redo accessible par Ctrl+Z / Ctrl+Y
 */
import { StateCreator, StoreApi } from 'zustand';
import { AppState, AppActions } from './StateService';
import { logger } from '../utils/ProfessionalLogger';

// Interface pour le slice d'historique dans le store
export interface HistorySlice {
  past: AppState['shapes'][];
  present: AppState['shapes'] | null;
  future: AppState['shapes'][];
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  commitHistory: () => void; // Pour sauvegarder manuellement l'historique à la fin d'une action utilisateur
}

// Extension du type du store
type StoreWithHistory = AppState & AppActions & HistorySlice;

// Type pour l'état historique (snapshot des shapes uniquement)
type HistorySnapshot = AppState['shapes'];

// État de l'historique (hors du store pour éviter les cycles)
let historyState = {
  past: [] as HistorySnapshot[],
  present: null as HistorySnapshot | null,
  future: [] as HistorySnapshot[],
  isUndoRedoInProgress: false, // Flag pour éviter d'enregistrer undo/redo dans l'historique
};

// Configuration du middleware
const MAX_HISTORY_SIZE = 50; // Limite de l'historique

/**
 * Crée une copie profonde des shapes pour l'historique
 */
function deepCloneShapes(shapes: AppState['shapes']): HistorySnapshot {
  return JSON.parse(JSON.stringify(shapes)) as HistorySnapshot;
}

/**
 * Compare deux snapshots pour détecter les changements
 */
function areShapesEqual(a: HistorySnapshot, b: HistorySnapshot): boolean {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Middleware personnalisé pour gérer l'historique des 'shapes' uniquement
 * (On ne veut pas annuler le zoom ou l'ouverture des panneaux)
 */
export const zustandHistoryMiddleware = <T extends StoreWithHistory>(
  config: StateCreator<T>
): StateCreator<T> => {
  return (set, get, api) => {
    // P2.7 : cast pour autoriser la signature devtools `set(partial, replace, action)`
    // (3ème arg ignoré sans devtools middleware, utilisé comme label pour debug).

    const setWithAction = set as unknown as (
      partial: unknown,
      replace?: boolean,
      action?: string
    ) => void;

    // Réinitialiser l'historique
    historyState = {
      past: [],
      present: null,
      future: [],
      isUndoRedoInProgress: false,
    };

    // Fonction undo: Restaure l'état précédent depuis past[]
    const undo = () => {
      if (historyState.past.length === 0) {
        logger.debug('↶ Aucune action à annuler');
        return;
      }

      const currentState = get();
      const currentShapes = currentState.shapes;

      // Prendre le dernier état du passé
      const previousShapes = historyState.past[historyState.past.length - 1];
      const newPast = historyState.past.slice(0, historyState.past.length - 1);

      // Mettre à jour l'historique
      historyState.past = newPast;
      historyState.future = [
        deepCloneShapes(currentShapes), // Sauvegarder l'état actuel dans le futur
        ...historyState.future,
      ];
      historyState.present = deepCloneShapes(previousShapes);
      historyState.isUndoRedoInProgress = true;

      // Mettre à jour le store (sans ré-enregistrer dans l'historique)
      setWithAction(
        {
          shapes: previousShapes,
          history: {
            canUndo: newPast.length > 0,
            canRedo: true,
            historyLength: newPast.length,
          },
        } as Partial<T>,
        false,
        'undo'
      );

      historyState.isUndoRedoInProgress = false;
      logger.info(`↶ Annulation: ${newPast.length} état(s) restant(s) dans le passé`);
    };

    // Fonction redo: Restaure l'état suivant depuis future[]
    const redo = () => {
      if (historyState.future.length === 0) {
        logger.debug('↷ Aucune action à rétablir');
        return;
      }

      const currentState = get();
      const currentShapes = currentState.shapes;

      // Prendre le premier état du futur
      const nextShapes = historyState.future[0];
      const newFuture = historyState.future.slice(1);

      // Mettre à jour l'historique
      historyState.past = [
        ...historyState.past,
        deepCloneShapes(currentShapes), // Sauvegarder l'état actuel dans le passé
      ];
      historyState.future = newFuture;
      historyState.present = deepCloneShapes(nextShapes);
      historyState.isUndoRedoInProgress = true;

      // Mettre à jour le store (sans ré-enregistrer dans l'historique)
      setWithAction(
        {
          shapes: nextShapes,
          history: {
            canUndo: true,
            canRedo: newFuture.length > 0,
            historyLength: historyState.past.length + 1,
          },
        } as Partial<T>,
        false,
        'redo'
      );

      historyState.isUndoRedoInProgress = false;
      logger.info(`↷ Rétablissement: ${newFuture.length} état(s) restant(s) dans le futur`);
    };

    // Fonction commitHistory: Sauvegarde explicite de l'historique
    // À appeler à la fin d'une action utilisateur (drag end, transform end, delete, etc.)
    const commitHistory = () => {
      // Ne pas enregistrer si un undo/redo est en cours
      if (historyState.isUndoRedoInProgress) {
        return;
      }

      const currentState = get();
      if (!currentState || !('shapes' in currentState)) {
        return;
      }

      const currentShapes = currentState.shapes;

      // Éviter les doublons (si rien n'a changé depuis le dernier commit)
      if (historyState.present && areShapesEqual(historyState.present, currentShapes)) {
        logger.debug('📝 Aucun changement détecté, skip commit');
        return; // Aucun changement détecté
      }

      // Sauvegarder l'état actuel dans le passé
      if (historyState.present) {
        historyState.past.push(deepCloneShapes(historyState.present));
      } else {
        // Premier commit: sauvegarder l'état initial
        historyState.past.push(deepCloneShapes(currentShapes));
      }

      // Limiter la taille de l'historique
      if (historyState.past.length > MAX_HISTORY_SIZE) {
        historyState.past.shift();
      }

      // Mettre à jour le présent
      historyState.present = deepCloneShapes(currentShapes);

      // Reset du futur (on ne peut plus redo après une nouvelle action)
      historyState.future = [];

      // Mise à jour des flags UI
      setWithAction(
        {
          history: {
            canUndo: historyState.past.length > 0,
            canRedo: false,
            historyLength: historyState.past.length,
          },
        } as Partial<T>,
        false,
        'history/commit'
      );

      logger.debug(`📝 Historique sauvegardé. États passés: ${historyState.past.length}`);
    };

    // Fonctions utilitaires
    const canUndo = () => historyState.past.length > 0;
    const canRedo = () => historyState.future.length > 0;

    // Surcharger la fonction set pour intercepter les changements
    // NOTE: On n'enregistre plus automatiquement dans l'historique
    // L'application doit appeler commitHistory() explicitement sur les événements End
    // P2.7 : signature étendue pour devtools (3ème arg = action label)
    const patchedSet = ((partial: unknown, replace?: boolean, action?: string) => {
      // Si c'est un undo/redo, ne pas enregistrer dans l'historique
      if (historyState.isUndoRedoInProgress || action === 'undo' || action === 'redo') {
        return setWithAction(partial, replace, action);
      }

      // Appliquer le changement directement (sans auto-commit)
      return setWithAction(partial, replace, action);
    }) as unknown as StoreApi<T>['setState'];

    // Créer le store avec les nouvelles méthodes injectées
    const store = config(patchedSet, get, {
      ...api,
      undo,
      redo,
      canUndo,
      canRedo,
      commitHistory,
    } as unknown as StoreApi<T> & {
      undo: () => void;
      redo: () => void;
      canUndo: () => boolean;
      canRedo: () => boolean;
      commitHistory: () => void;
    });

    // Exposer les fonctions d'historique dans le store retourné
    return Object.assign(store, {
      undo,
      redo,
      canUndo,
      canRedo,
      commitHistory,
    }) as T;
  };
};
